import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { all, call, put, takeLatest } from "redux-saga/effects";
import { facilitiesApi } from "../../services/facilities.api";
import { DashboardAnalytics } from "../../types/dashboard.types";
import { Doctor } from "../../types/doctor.definition";
import { HealthcareFacilityStats } from "../../types/facility.definition";
import {
  getHealthcareFacilityAlerts,
  getHealthcareFacilityDashboardAnalytics,
  getHealthcareFacilityRecentActivities,
  getHealthcareFacilityTodaySchedule,
  getWorkingDoctorsTodayByFacility,
} from "../../webclients/facility.client";
import {
  fetchAnalyticsFailure,
  fetchAnalyticsRequest,
  fetchAnalyticsSuccess,
} from "../reducers/dashboardAnalyticsSlice";
import {
  Activity,
  Alert,
  fetchAlertsFailure,
  fetchAlertsRequest,
  fetchAlertsSuccess,
  fetchDashboardDataRequest,
  fetchRecentActivityFailure,
  fetchRecentActivityRequest,
  fetchRecentActivitySuccess,
  fetchStatsFailure,
  fetchStatsRequest,
  fetchStatsSuccess,
  fetchTodayScheduleFailure,
  fetchTodayScheduleRequest,
  fetchTodayScheduleSuccess,
  fetchWorkingDoctorsFailure,
  fetchWorkingDoctorsRequest,
  fetchWorkingDoctorsSuccess,
  ScheduleItem,
} from "../reducers/dashboardSlice";

// Fetch stats saga
function* fetchStatsSaga({
  payload,
}: PayloadAction<{ facilityId: string | number }>): Generator<
  unknown,
  void,
  HealthcareFacilityStats
> {
  try {
    const stats = yield call(
      facilitiesApi.fetchFacilityStats,
      Number(payload.facilityId)
    );
    yield put(fetchStatsSuccess({ stats, facilityId: payload.facilityId }));
  } catch (error) {
    yield put(
      fetchStatsFailure(
        error instanceof Error ? error.message : "Failed to fetch stats"
      )
    );
  }
}

// Fetch working doctors saga - uses optimized endpoint that returns doctors with available slots today
function* fetchWorkingDoctorsSaga({
  payload,
}: PayloadAction<{ facilityId: string | number }>): Generator<
  unknown,
  void,
  unknown
> {
  try {
    const response = (yield call(
      getWorkingDoctorsTodayByFacility,
      Number(payload.facilityId)
    )) as AxiosResponse<Doctor[]>;
    const workingDoctors: Doctor[] = response.data || [];

    yield put(fetchWorkingDoctorsSuccess({ doctors: workingDoctors }));
  } catch (error) {
    yield put(
      fetchWorkingDoctorsFailure(
        error instanceof Error
          ? error.message
          : "Failed to fetch working doctors"
      )
    );
  }
}

// Fetch today's schedule saga
function* fetchTodayScheduleSaga({
  payload,
}: PayloadAction<{ facilityId: string | number }>): Generator<
  unknown,
  void,
  unknown
> {
  try {
    const responseRaw = yield call(
      getHealthcareFacilityTodaySchedule,
      Number(payload.facilityId)
    );
    const response = responseRaw as AxiosResponse<{ schedule: ScheduleItem[] }>;
    const scheduleItems: ScheduleItem[] = response.data?.schedule || [];

    yield put(fetchTodayScheduleSuccess({ schedule: scheduleItems }));
  } catch (error) {
    yield put(
      fetchTodayScheduleFailure(
        error instanceof Error
          ? error.message
          : "Failed to fetch today's schedule"
      )
    );
  }
}

// Fetch recent activity saga
function* fetchRecentActivitySaga({
  payload,
}: PayloadAction<{ facilityId: string | number }>): Generator<
  unknown,
  void,
  unknown
> {
  try {
    const responseRaw = yield call(
      getHealthcareFacilityRecentActivities,
      Number(payload.facilityId)
    );
    const response = responseRaw as AxiosResponse<{ activities: Activity[] }>;
    const activities: Activity[] = response.data?.activities || [];

    yield put(fetchRecentActivitySuccess({ activities }));
  } catch (error) {
    yield put(
      fetchRecentActivityFailure(
        error instanceof Error
          ? error.message
          : "Failed to fetch recent activity"
      )
    );
  }
}

// Fetch alerts saga
function* fetchAlertsSaga({
  payload,
}: PayloadAction<{ facilityId: string | number }>): Generator<
  unknown,
  void,
  unknown
> {
  try {
    const responseRaw = yield call(
      getHealthcareFacilityAlerts,
      Number(payload.facilityId)
    );
    const response = responseRaw as AxiosResponse<{ alerts: Alert[] }>;
    const alerts: Alert[] = response.data?.alerts || [];

    yield put(fetchAlertsSuccess({ alerts }));
  } catch (error) {
    yield put(
      fetchAlertsFailure(
        error instanceof Error ? error.message : "Failed to fetch alerts"
      )
    );
  }
}

// Fetch dashboard analytics saga
function* fetchAnalyticsSaga({
  payload,
}: PayloadAction<{
  facilityId: string | number;
  timePeriod?: string;
}>): Generator<unknown, void, unknown> {
  try {
    const responseRaw = yield call(
      getHealthcareFacilityDashboardAnalytics,
      Number(payload.facilityId),
      payload.timePeriod || "all"
    );
    const response = responseRaw as AxiosResponse<DashboardAnalytics>;
    const analytics: DashboardAnalytics = response.data;

    yield put(fetchAnalyticsSuccess({ analytics }));
  } catch (error) {
    yield put(
      fetchAnalyticsFailure(
        error instanceof Error
          ? error.message
          : "Failed to fetch dashboard analytics"
      )
    );
  }
}

// Combined dashboard data fetch saga
function* fetchDashboardDataSaga({
  payload,
}: PayloadAction<{ facilityId: string | number }>): Generator<
  unknown,
  void,
  unknown
> {
  try {
    // Fetch all dashboard data in parallel
    // NOTE: Analytics are NOT fetched here - they are only loaded when user
    // navigates to "Reports & Analytics" page for better performance
    yield all([
      call(fetchStatsSaga, {
        type: fetchStatsRequest.type,
        payload,
      } as PayloadAction<{ facilityId: string | number }>),
      call(fetchWorkingDoctorsSaga, {
        type: fetchWorkingDoctorsRequest.type,
        payload,
      } as PayloadAction<{ facilityId: string | number }>),
      call(fetchTodayScheduleSaga, {
        type: fetchTodayScheduleRequest.type,
        payload,
      } as PayloadAction<{ facilityId: string | number }>),
      call(fetchRecentActivitySaga, {
        type: fetchRecentActivityRequest.type,
        payload,
      } as PayloadAction<{ facilityId: string | number }>),
      call(fetchAlertsSaga, {
        type: fetchAlertsRequest.type,
        payload,
      } as PayloadAction<{ facilityId: string | number }>),
      // Analytics removed - now only fetched from ReportsAnalytics component
    ]);
  } catch (error) {
    // Individual sagas handle their own errors
    console.error("Error fetching dashboard data:", error);
  }
}

export function* watchDashboard() {
  yield takeLatest(fetchStatsRequest.type, fetchStatsSaga);
  yield takeLatest(fetchWorkingDoctorsRequest.type, fetchWorkingDoctorsSaga);
  yield takeLatest(fetchTodayScheduleRequest.type, fetchTodayScheduleSaga);
  yield takeLatest(fetchRecentActivityRequest.type, fetchRecentActivitySaga);
  yield takeLatest(fetchAlertsRequest.type, fetchAlertsSaga);
  yield takeLatest(fetchAnalyticsRequest.type, fetchAnalyticsSaga);
  yield takeLatest(fetchDashboardDataRequest.type, fetchDashboardDataSaga);
}
